import { css, cx } from '@emotion/css';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { PowerIcon, WaterIcon } from '../../../shared/assets/icons';
import InfoGroup from '../../../shared/components/InfoGroup';
import { Colors } from '../../../shared/styles/colors';
import { h2Style, labelStyle, p2Style } from '../../../shared/styles/text';
import { PowerTypeEnum, SourceTypeEnum } from '../../../shared/types/enums';
import { ReceiptItem } from '../../../shared/types/server-types';
import { getTimeDiffFormated } from '../../../shared/utils/timeDiffFormated';
import { Radius } from '../../../shared/styles/tokens';

interface IReceiptItemOverviewCardProps {
  item: ReceiptItem;
  last: boolean;
  showVehicle: boolean;
}

export const ReceiptItemOverviewCard = (
  props: IReceiptItemOverviewCardProps,
): JSX.Element => {
  const { item, last, showVehicle } = props;

  const formatedDuration = getTimeDiffFormated(item.startDate, item.endDate);

  const infoGroupElements = [
    item.sourceType === SourceTypeEnum.VALVE
      ? {
          label: 'consumption',
          value: `${item.consumption.toFixed(3)} m³`,
          Icon: InformationCircleIcon,
          iconClassName: infoIconStyle,
        }
      : {
          label: 'consumption',
          value: `${item.consumption.toFixed(3)} kWh`,
          Icon: InformationCircleIcon,
          iconClassName: infoIconStyle,
        },
    { label: 'duration', value: formatedDuration },
    {
      label: 'total',
      value: `${(item.consumption * item.tariff).toFixed(2)} EUR`,
    },
  ];

  const accent =
    item.sourceType === SourceTypeEnum.SOCKET
      ? item.phaseType === PowerTypeEnum.SINGLE
        ? Colors.MONO_PHASE
        : Colors.THREE_PHASE
      : Colors.CAPRI_BLUE;

  return (
    <div className={cx(itemCardStyle(accent), last && lastItemCardStyle)}>
      <div className={itemCardHeaderStyle}>
        <div>
          <p className={p2Style}>
            ID {item.pedestalIdentifier.toUpperCase()}{' '}
            {item.pedestal ? `(${item.pedestal?.location.name})` : ''}
          </p>
          <h2 className={h2Style}>{item.service}</h2>
        </div>
        {item.sourceType === SourceTypeEnum.SOCKET ? (
          <PowerIcon className={iconStyle(accent)} />
        ) : (
          <WaterIcon className={iconStyle(accent)} />
        )}
      </div>
      <InfoGroup
        className={infoGroupStyle}
        elements={
          showVehicle
            ? [
                ...infoGroupElements,
                { label: 'vehicle', value: item.vehicleRegistration },
              ]
            : infoGroupElements
        }
      />
    </div>
  );
};

const itemCardStyle = (accent: string) => css`
  position: relative;
  background-color: ${accent}0f;
  border: 1.5px solid ${accent}40;
  border-radius: ${Radius.lg};
  width: 100%;
  padding: 1rem 1rem 0.5rem 1rem;
  > label {
    ${labelStyle.toString()}
    color: ${Colors.TEXT_SECONDARY};
    text-transform: capitalize;
  }
`;

const lastItemCardStyle = css`
  margin-bottom: 1rem;
`;

const itemCardHeaderStyle = css`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  text-transform: capitalize;
  margin-bottom: 0.5rem;

  > div {
    min-width: 0;
  }

  > div > p {
    color: ${Colors.TEXT_SECONDARY};
    overflow-wrap: anywhere;
  }
`;

const infoGroupStyle = css`
  margin: 0.5rem 0 0;
`;

const iconStyle = (color: string) => css`
  width: 3rem;
  height: 3rem;
  flex: 0 0 auto;
  align-self: center;
  > path {
    fill: ${color};
  }
`;

const infoIconStyle = css`
  width: 24px;
  color: ${Colors.CAPRI_BLUE};
`;
